import { useState, useEffect, createContext, useContext } from 'react';

// Create AuthContext
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [access_token, setAccessToken] = useState(localStorage.getItem('access_token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));
  const [userType, setUserType] = useState(localStorage.getItem('user_type'));
  const [regStatus, setRegStatus] = useState(localStorage.getItem('reg_status'));
  const [userId, setUserId] = useState(localStorage.getItem('user_id'));
  const [userEmail, setUserEmail] = useState(localStorage.getItem('user_email'));

  // Initialize regStatus properly on component mount
  useEffect(() => {
    const storedRegStatus = localStorage.getItem('reg_status');
    if (storedRegStatus !== null) {
      setRegStatus(storedRegStatus);
    }
  }, []);

  // Function to update auth state and localStorage
  const login = (userData) => {
    return new Promise((resolve) => {
      // Update localStorage first - JWT tokens use 'access' and 'refresh' fields
      localStorage.setItem('access_token', userData.access);
      localStorage.setItem('refresh_token', userData.refresh);
      localStorage.setItem('user_id', userData.user_id);
      localStorage.setItem('user_type', userData.user_type);
      localStorage.setItem('user_email', userData.email);
      
      if (userData.reg_status !== undefined) {
        localStorage.setItem('reg_status', userData.reg_status);
      }
      // Handle accommodation provider data
      if (userData.user_type === 'accommodation_provider') {
        if (userData.provider_id !== undefined) {
          localStorage.setItem('provider_id', userData.provider_id);
        }
        if (userData.provider_type !== undefined) {
          localStorage.setItem('provider_type', userData.provider_type);
        }
        if (userData.provider_name !== undefined) {
          localStorage.setItem('provider_name', userData.provider_name);
        }
        if (userData.representative_name !== undefined) {
          localStorage.setItem('representative_name', userData.representative_name);
        }
        // Legacy support
        if (userData.type !== undefined) {
          localStorage.setItem('provider_type', userData.type);
        }
      }

      // Handle tutoring provider data
      if (userData.user_type === 'tutoring_agent') {
        if (userData.provider_code !== undefined) {
          localStorage.setItem('provider_code', userData.provider_code);
        }
        if (userData.provider_id !== undefined) {
          localStorage.setItem('provider_id', userData.provider_id);
        }
        if (userData.provider_type !== undefined) {
          localStorage.setItem('provider_type', userData.provider_type);
        }
        if (userData.provider_name !== undefined) {
          localStorage.setItem('provider_name', userData.provider_name);
        }
        if (userData.representative_name !== undefined) {
          localStorage.setItem('representative_name', userData.representative_name);
        }
      }

      // Update state synchronously
      setAccessToken(userData.access);
      setRefreshToken(userData.refresh);
      setUserType(userData.user_type);
      setRegStatus(userData.reg_status);
      setUserId(userData.user_id);
      setUserEmail(userData.email);
      
      // Use setTimeout to ensure state updates are processed
      setTimeout(() => {
        resolve();
      }, 50);
    });
  };

  // Function to logout
  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_type');
    localStorage.removeItem('user_email');
    localStorage.removeItem('reg_status');
    localStorage.removeItem('provider_id');
    localStorage.removeItem('provider_type');
    localStorage.removeItem('provider_name');
    localStorage.removeItem('representative_name');
    localStorage.removeItem('provider_code');

    // Update state
    setAccessToken(null);
    setRefreshToken(null);
    setUserType(null);
    setRegStatus(null);
    setUserId(null);
    setUserEmail(null);
  };

  // Check for changes in localStorage (for cases where localStorage is updated directly)
  useEffect(() => {
    const handleStorageChange = () => {
      setAccessToken(localStorage.getItem('access_token'));
      setUserType(localStorage.getItem('user_type'));
      setRegStatus(localStorage.getItem('reg_status'));
      setUserId(localStorage.getItem('user_id'));
      setUserEmail(localStorage.getItem('user_email'));
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value = {
    access_token,
    userType,
    regStatus,
    userId,
    userEmail,
    login,
    logout,
    isAuthenticated: !!access_token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
